import { toast } from "react-toastify";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import i18next from "i18next";
dayjs.extend(customParseFormat);
dayjs.extend(utc);

/**
 * Show toast message
 * @param {string} message - The message to display (can be a translation key or already-translated string)
 * @param {object} options - Options for the toast. Use interpolation: { key: value } for translation placeholders (e.g. {{key}}).
 */
export const showToast = (message, options = {}) => {
	if (!message) return;

	const { type = "info", interpolation, ...toastOptions } = options;

	// Translate with interpolation when provided (so {{userCount}}, {{name}}, etc. are replaced)
	const useInterpolation =
		interpolation &&
		typeof interpolation === "object" &&
		!Array.isArray(interpolation);
	const displayMessage = useInterpolation
		? i18next.t(message, interpolation)
		: i18next.t(message, message);

	// Use the appropriate toast type
	const toastConfig = {
		position: "bottom-center",
		autoClose: 3000,
		hideProgressBar: true,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
		theme: "light",
		...toastOptions,
	};

	switch (type) {
		case "success":
			toast.success(displayMessage, toastConfig);
			break;
		case "error":
			toast.error(displayMessage, toastConfig);
			break;
		case "warning":
			toast.warning(displayMessage, toastConfig);
			break;
		default:
			toast.info(displayMessage, toastConfig);
	}
};

export const isAndroidDevice = () => {
	return navigator.userAgent.match(/Android/);
};

export const isMobileDevice = () => {
	const toMatch = [/Android/i, /iPhone/i, /iPod/i, /Windows Phone/i];

	return toMatch.some((toMatchItem) => {
		return navigator.userAgent.match(toMatchItem);
	});
};

export const showInfo = (t, message) => {
	if (message) {
		const options = { type: "info" };
		if (isMobileDevice()) {
			options.position = toast.POSITION.TOP_CENTER;
		}
		showToast(t(message), options);
		return;
	}
};

export const showSuccess = (t, message) => {
	if (message) {
		showToast(t(message), { type: "success" });
		return;
	}
};

export const showError = (_t, error) => {
	// Normalize the error to the backend reject payload ({ message, data }).
	// It arrives in three shapes depending on the caller:
	//   - raw axios error (not unwrapped by the interceptor): error.response.data
	//   - RTK Query unwrap() error:                           error.data (a payload with its own .message)
	//   - already-unwrapped interceptor / thunk reject:       error itself (.message + .data interpolation)
	const payload =
		error?.response?.data ??
		(error?.data?.message ? error.data : null) ??
		error ??
		{};
	const message = payload?.message || "API_ERROR";
	const rawData = payload?.data ?? null;
	const interpolation =
		rawData && typeof rawData === "object" && !Array.isArray(rawData)
			? rawData
			: null;
	showToast(message, {
		type: "error",
		...(interpolation ? { interpolation } : {}),
	});
};

const DATE_FORMAT = "DD.MM.YYYY";
const DATE_TIME_FORMAT = "DD.MM.YYYY HH:mm:ss";
const CURRENCY_FORMAT_OPTION = {
	style: "currency",
	currency: "EUR",
	minimumFractionDigits: 2,
};

export const getFormattedDate = (inputDate, containTime = false) => {
	if (!inputDate) {
		return "";
	}
	let outputDateFormat = containTime ? DATE_TIME_FORMAT : DATE_FORMAT;
	return dayjs.utc(inputDate).local().format(outputDateFormat);
};

export const getWeekdayName = (inputDate) => {
	if (!inputDate) {
		return "";
	}
	return inputDate.toLocaleDateString("de-DE", { weekday: "long" });
};

export const readFileAsync = (file) => {
	return new Promise((resolve, reject) => {
		let reader = new FileReader();

		reader.onload = () => {
			resolve(reader.result);
		};

		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
};

export const formatValueToLocale = (
	data,
	isCurrency = false,
	minimumFractionDigits = 2,
	maximumFractionDigits = 2,
) => {
	const num = Number.parseFloat(data);
	if (Number.isNaN(num)) {
		return "";
	}
	return num.toLocaleString(
		"de-DE",
		isCurrency
			? {
					...CURRENCY_FORMAT_OPTION,
					minimumFractionDigits,
					maximumFractionDigits,
				}
			: {
					minimumFractionDigits,
					maximumFractionDigits,
				},
	);
};

// New utility specifically for quantity values
export const formatQuantityToLocale = (value) => {
	const num = Number.parseFloat(value);
	if (Number.isNaN(num)) {
		return "";
	}
	return formatValueToLocale(num, false, 3, 3);
};

export const roundToTwo = (num) => {
	return Math.round((Number(num) + Number.EPSILON) * 100) / 100;
};

export const formatCurrencyToLocale = (value) => {
	return `${formatValueToLocale(roundToTwo(value))} €`;
};

export const extractValues = (mappings) => {
	return Object.keys(mappings);
};

export const lookupValue = (mappings, key) => {
	return mappings[key];
};

export const getCellValue = (fieldValue, attribute) => {
	let value = fieldValue;
	if (attribute?.attributeType?.name === "INPUT") {
		if (attribute?.attributeFormat?.name === "DATE") {
			value = getFormattedDate(value);
		}
		if (attribute?.attributeFormat?.name === "DATETIME") {
			value = getFormattedDate(value, true);
		}
		if (attribute?.attributeFormat?.name === "NUMBER") {
			if (attribute?.externalSystemIdentifier === "WEIGHT_P") {
				value = formatValueToLocale(value, false, 3);
			}
		}
		if (attribute?.attributeFormat?.checkbox === "CHECKBOX") {
			value = !!+value;
		}
	}
	return value || "";
};

export const stableSort = (array, comparator) => {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
};

export const descendingComparator = (a, b, orderBy) => {
	let aVal = a ? a.orderInfo[orderBy].value : null;
	let bVal = b ? b.orderInfo[orderBy].value : null;

	if (typeof aVal === "string" || aVal instanceof String) {
		aVal = aVal.toLowerCase();
	}

	if (typeof bVal === "string" || bVal instanceof String) {
		bVal = bVal.toLowerCase();
	}

	if (bVal < aVal) {
		return -1;
	}
	if (bVal > aVal) {
		return 1;
	}
	return 0;
};

export function getComparator(order, orderBy) {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}
